import React from 'react';
import type { User } from '../../../../shared/types/index.js';

interface RoleScopingBannerProps {
  user: User | null;
  scopeMode: 'me' | 'public';
  onSetScopeMode: (mode: 'me' | 'public') => void;
}

export const RoleScopingBanner: React.FC<RoleScopingBannerProps> = ({
  user,
  scopeMode,
  onSetScopeMode,
}) => {
  return (
    <div className="space-y-3">
      {/* Role-based Data Scoping Banner */}
      {user ? (
        <div
          className={`p-3.5 rounded-2xl border flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs ${
            user.role === 'penulis'
              ? 'bg-sky-50/70 border-sky-200 text-sky-900'
              : user.role === 'penerbit'
              ? 'bg-purple-50/70 border-purple-200 text-purple-900'
              : 'bg-emerald-50/70 border-emerald-200 text-emerald-900'
          }`}
        >
          <div className="flex items-center gap-2.5">
            <span
              className={`px-2.5 py-1 rounded-lg font-bold uppercase tracking-wider text-[10px] ${
                user.role === 'penulis'
                  ? 'bg-sky-200 text-sky-950'
                  : user.role === 'penerbit'
                  ? 'bg-purple-200 text-purple-950'
                  : 'bg-emerald-200 text-emerald-950'
              }`}
            >
              Akses Role: {user.role}
            </span>
            <div>
              <p className="font-semibold">
                {user.role === 'penulis' && `Katalog Pribadi Penulis: ${user.name}`}
                {user.role === 'penerbit' &&
                  `Katalog Penerbit: ${user.organization || user.name} (Kode: ${
                    user.publisherCode || '8042'
                  })`}
                {user.role === 'admin' &&
                  'Pusat Registrasi UIBN Nasional (Akses Penuh Administrator)'}
              </p>
              <p className="text-[11px] opacity-80 mt-0.5">
                {scopeMode === 'public'
                  ? 'Sedang menampilkan seluruh buku publik ber-UIBN resmi aktif di tingkat nasional.'
                  : user.role === 'penulis'
                  ? 'Menampilkan hanya buku karya Anda. Data draf dan naskah penulis lain terlindungi secara privat.'
                  : user.role === 'penerbit'
                  ? 'Menampilkan naskah dan buku terbitan Anda. Data naskah penerbit lain terlindungi secara privat.'
                  : 'Menampilkan seluruh buku dari semua penerbit dan penulis di seluruh Indonesia.'}
              </p>
            </div>
          </div>

          {(user.role === 'penulis' || user.role === 'penerbit') && (
            <div className="flex items-center gap-1.5 shrink-0 bg-white/80 p-1 rounded-xl border border-current/20 shadow-2xs">
              <button
                type="button"
                onClick={() => onSetScopeMode('me')}
                className={`px-3 py-1 rounded-lg font-medium transition cursor-pointer ${
                  scopeMode === 'me'
                    ? 'bg-slate-900 text-white shadow-2xs'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                {user.role === 'penulis' ? 'Karya Saya' : 'Katalog Penerbit'}
              </button>
              <button
                type="button"
                onClick={() => onSetScopeMode('public')}
                className={`px-3 py-1 rounded-lg font-medium transition cursor-pointer ${
                  scopeMode === 'public'
                    ? 'bg-slate-900 text-white shadow-2xs'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                Katalog Publik Nasional
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="p-3.5 rounded-2xl border bg-slate-50 border-slate-200 flex items-center justify-between gap-3 text-xs text-slate-700">
          <div className="flex items-center gap-2">
            <span className="px-2 py-0.5 rounded-md font-bold uppercase tracking-wider text-[10px] bg-slate-200 text-slate-800">
              Katalog Publik
            </span>
            <p className="text-[11px] text-slate-600">
              Anda sedang mengakses katalog publik. Hanya buku yang telah disetujui & ber-UIBN resmi yang ditampilkan.
            </p>
          </div>
        </div>
      )}

      {/* 24-Hour Auto-Approve Policy Banner */}
      {user && (user.role === 'penulis' || user.role === 'penerbit') && (
        <div className="p-3 bg-indigo-50/70 border border-indigo-100/50 rounded-2xl flex items-center justify-between gap-3 text-xs text-indigo-950">
          <div className="flex items-center gap-2.5">
            <span className="flex h-2 w-2 relative">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-indigo-600"></span>
            </span>
            <p className="text-[11px] leading-relaxed">
              <span className="font-bold">Kebijakan Auto-Approve:</span> Setiap pengajuan UIBN dijamin akan disetujui paling lama <b>24 jam setelah diajukan</b> secara otomatis oleh sistem jika admin tidak mengambil tindakan.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
